/**
 * Dynamic Statistics Engine
 * Calculates real-time statistics for loyalty programs
 */

import { LoyaltyProgram } from '../types';
import APIService from './apiService';
import CurrencyService from './currencyService';

export interface PortfolioStats {
  totalValueINR: number;
  totalValueUSD: number;
  exchangeRate: number;
  lastUpdated: Date;
  bestProgram: {
    name: string;
    valuePerPoint: number;
  };
  worstProgram: {
    name: string;
    valuePerPoint: number;
  };
  averageValuePerPoint: number;
  expiringPointsValue: number;
  expiringIn30Days: LoyaltyProgram[];
}

export interface BestRouteValue {
  route: string;
  valuePerPointINR: number;
  description: string;
  canAfford: boolean;
}

class CalculationEngine {
  /**
   * Calculate comprehensive portfolio statistics with real exchange rates
   */
  static async calculatePortfolioStats(programs: LoyaltyProgram[]): Promise<PortfolioStats> {
    let totalValueUSD = 0;
    const programValues: Array<{name: string, valuePerPoint: number, points: number}> = [];
    const expiringIn30Days: LoyaltyProgram[] = [];
    let expiringPointsValueUSD = 0;

    // Calculate value for each program in USD first
    for (const program of programs) {
      // Get point value in USD (most loyalty programs quote values in USD)
      const pointValueUSD = this.getPointValueUSD(program.type, program.name);
      const programValueUSD = program.points * pointValueUSD;
      
      totalValueUSD += programValueUSD;
      
      programValues.push({
        name: program.name,
        valuePerPoint: pointValueUSD,
        points: program.points
      });

      // Check for expiring points (within 30 days)
      if (program.expiryDate) {
        const expiryDate = new Date(program.expiryDate);
        const now = new Date();
        const daysUntilExpiry = (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
        
        if (daysUntilExpiry <= 30 && daysUntilExpiry > 0) {
          expiringIn30Days.push(program);
          expiringPointsValueUSD += programValueUSD;
        }
      }
    }

    // Convert total USD value to INR using real exchange rates
    let totalValueINR = totalValueUSD * 83; // Fallback rate
    let exchangeRate = 83;
    let lastUpdated = new Date();

    try {
      console.log('💱 Converting portfolio value from USD to INR...');
      const conversion = await CurrencyService.convertCurrency(totalValueUSD, 'USD', 'INR');
      totalValueINR = conversion.converted.amount;
      exchangeRate = conversion.rate;
      lastUpdated = conversion.lastUpdated;
      console.log(`✅ Portfolio: $${totalValueUSD.toFixed(2)} → ₹${totalValueINR.toFixed(2)} @ ${exchangeRate}`);
    } catch (error) {
      console.warn('⚠️ Currency conversion failed, using fallback rate:', error);
    }

    // Find best and worst programs
    const sortedPrograms = programValues
      .filter(p => p.points > 0)
      .sort((a, b) => b.valuePerPoint - a.valuePerPoint);

    const bestProgram = sortedPrograms[0] || { name: 'None', valuePerPoint: 0 };
    const worstProgram = sortedPrograms[sortedPrograms.length - 1] || { name: 'None', valuePerPoint: 0 };

    // Calculate average value per point
    const totalPoints = programs.reduce((sum, p) => sum + p.points, 0);
    const averageValuePerPoint = totalPoints > 0 ? totalValueUSD / totalPoints : 0;

    return {
      totalValueINR: Math.round(totalValueINR),
      totalValueUSD: Math.round(totalValueUSD * 100) / 100,
      exchangeRate: Math.round(exchangeRate * 100) / 100,
      lastUpdated,
      bestProgram: {
        name: bestProgram.name,
        valuePerPoint: Math.round(bestProgram.valuePerPoint * 10000) / 10000
      },
      worstProgram: {
        name: worstProgram.name,
        valuePerPoint: Math.round(worstProgram.valuePerPoint * 10000) / 10000
      },
      averageValuePerPoint: Math.round(averageValuePerPoint * 10000) / 10000,
      expiringPointsValue: Math.round(expiringPointsValueUSD * exchangeRate),
      expiringIn30Days
    };
  }

  /**
   * Get point value in USD based on program type and name
   * Uses industry-standard valuations from TPG, NerdWallet, etc.
   */
  private static getPointValueUSD(type: string, programName: string): number {
    const programName_lower = programName.toLowerCase();
    
    // Airline programs (cents per mile/point)
    if (type === 'airline') {
      if (programName_lower.includes('united')) return 0.012; // 1.2¢ per United mile
      if (programName_lower.includes('american')) return 0.015; // 1.5¢ per AA mile
      if (programName_lower.includes('delta')) return 0.012; // 1.2¢ per Delta mile
      if (programName_lower.includes('emirates')) return 0.018; // 1.8¢ per Emirates mile
      if (programName_lower.includes('lufthansa')) return 0.016; // 1.6¢ per Lufthansa mile
      return 0.013; // Default airline value
    }
    
    // Credit card programs
    if (type === 'credit_card') {
      if (programName_lower.includes('chase sapphire')) return 0.020; // 2¢ via travel portal
      if (programName_lower.includes('amex')) return 0.018; // 1.8¢ average
      if (programName_lower.includes('hdfc')) return 0.008; // ₹0.25 per point ≈ 0.8¢
      if (programName_lower.includes('axis')) return 0.006; // ₹0.20 per mile ≈ 0.6¢
      if (programName_lower.includes('sbi')) return 0.005; // ₹0.15 per point ≈ 0.5¢
      if (programName_lower.includes('icici')) return 0.004; // ₹0.12 per point ≈ 0.4¢
      return 0.010; // Default credit card value
    }
    
    // Hotel programs
    if (type === 'hotel') {
      if (programName_lower.includes('marriott')) return 0.008; // 0.8¢ per Marriott point
      if (programName_lower.includes('hilton')) return 0.005; // 0.5¢ per Hilton point
      if (programName_lower.includes('hyatt')) return 0.019; // 1.9¢ per Hyatt point
      if (programName_lower.includes('ihg')) return 0.005; // 0.5¢ per IHG point
      return 0.007; // Default hotel value
    }
    
    return 0.010; // Default fallback value
  }

  /**
   * Calculate best route value for airline programs
   */
  static async calculateBestRouteValue(airlinePrograms: LoyaltyProgram[]): Promise<BestRouteValue> {
    if (airlinePrograms.length === 0) {
      return {
        route: 'No airline programs',
        valuePerPointINR: 0,
        description: 'Add airline programs to see recommendations',
        canAfford: false
      };
    }

    try {
      const userPrograms = airlinePrograms.map(p => ({
        name: p.name,
        points: p.points,
        type: p.type
      }));

      const bestRoutes = await APIService.calculateBestValueRoutes(userPrograms);
      
      if (bestRoutes.length === 0) {
        return {
          route: 'No routes available',
          valuePerPointINR: 0,
          description: 'Unable to calculate routes',
          canAfford: false
        };
      }

      const bestRoute = bestRoutes[0];
      
      return {
        route: bestRoute.route,
        valuePerPointINR: bestRoute.valuePerPoint,
        description: `${bestRoute.savings} savings vs cash`,
        canAfford: bestRoute.canAfford
      };
    } catch (error) {
      console.error('Error calculating best route value:', error);
      return {
        route: 'Calculation error',
        valuePerPointINR: 0,
        description: 'Unable to calculate at this time',
        canAfford: false
      };
    }
  }

  /**
   * Calculate transfer opportunities based on user's programs
   */
  static calculateTransferOpportunities(
    creditCards: LoyaltyProgram[],
    airlines: LoyaltyProgram[]
  ): Array<{
    from: string;
    to: string;
    ratio: string;
    transferTime: string;
    relevance: 'high' | 'medium' | 'low';
    bonus?: string;
  }> {
    const allOpportunities = APIService.getTransferOpportunities();
    
    // Filter and rank opportunities based on user's actual programs
    const userCardNames = creditCards.map(card => card.name.toLowerCase());
    const userAirlineNames = airlines.map(airline => airline.name.toLowerCase());
    
    return allOpportunities.map(opp => {
      let relevance: 'high' | 'medium' | 'low' = 'low';
      
      // Check if user has the source credit card
      const hasSourceCard = userCardNames.some(cardName => 
        opp.from.toLowerCase().includes(cardName.split(' ')[0]) || 
        cardName.includes(opp.from.toLowerCase().split(' ')[0])
      );
      
      // Check if user has the target airline program
      const hasTargetAirline = userAirlineNames.some(airlineName => 
        opp.to.toLowerCase().includes(airlineName.split(' ')[0]) || 
        airlineName.includes(opp.to.toLowerCase().split(' ')[0])
      );
      
      if (hasSourceCard && hasTargetAirline) {
        relevance = 'high';
      } else if (hasSourceCard || hasTargetAirline) {
        relevance = 'medium';
      }
      
      return {
        ...opp,
        relevance
      };
    }).sort((a, b) => {
      // Sort by relevance (high first), then by bonus availability
      const relevanceOrder = { high: 3, medium: 2, low: 1 };
      const aScore = relevanceOrder[a.relevance] + (a.bonus ? 1 : 0);
      const bScore = relevanceOrder[b.relevance] + (b.bonus ? 1 : 0);
      return bScore - aScore;
    });
  }

  /**
   * Generate personalized flight deals based on user's programs
   */
  static async generatePersonalizedDeals(
    airlinePrograms: LoyaltyProgram[]
  ): Promise<Array<{
    route: string;
    class: string;
    airline: string;
    points: number;
    cash: number;
    savings: string;
    canAfford: boolean;
    recommendationReason: string;
  }>> {
    if (airlinePrograms.length === 0) {
      return [];
    }

    const userPrograms = airlinePrograms.map(p => ({ name: p.name, points: p.points, type: p.type }));
    const bestRoutes = await APIService.calculateBestValueRoutes(userPrograms);
    
    // Get diverse routes from different best value routes
    const diverseRoutes = [];
    const usedDestinations = new Set<string>();
    
    for (let i = 0; i < Math.min(bestRoutes.length, 6) && diverseRoutes.length < 3; i++) {
      const route = bestRoutes[i];
      const destination = route.route.split(' → ')[1];
      
      if (!usedDestinations.has(destination)) {
        usedDestinations.add(destination);
        const userAirline = airlinePrograms.find(p => p.points >= route.pointsNeeded)?.name || 'American AAdvantage';
        
        let recommendationReason = '';
        if (route.canAfford) {
          recommendationReason = `You have enough points! Great value at ${route.valuePerPoint}₹/point`;
        } else {
          const shortfall = route.pointsNeeded - airlinePrograms.reduce((sum, p) => sum + p.points, 0);
          recommendationReason = `Best value route. Need ${shortfall.toLocaleString()} more points`;
        }
        
        // Randomize the class for variety
        const classes = ['Economy', 'Business', 'Premium Economy'];
        const classIndex: number = diverseRoutes.length % classes.length;
        
        // Adjust points and cash based on class
        const classMultipliers: Record<string, number> = { Economy: 1, 'Premium Economy': 1.5, Business: 2.5 };
        const multiplier: number = classMultipliers[classes[classIndex]] || 1;
        
        diverseRoutes.push({
          route: route.route,
          class: classes[classIndex],
          airline: userAirline,
          points: Math.round(route.pointsNeeded * multiplier),
          cash: Math.round(route.cashAlternative * 0.6), // Add some cash component
          savings: route.savings,
          canAfford: route.canAfford,
          recommendationReason
        });
      }
    }
    
    // If we don't have enough diverse routes, fill with more dynamic routes
    if (diverseRoutes.length < 3) {
      const fallbackRoutes = [
        { route: 'Mumbai → Singapore', destination: 'Singapore', points: 18000, cash: 15000, class: 'Economy' },
        { route: 'Delhi → Dubai', destination: 'Dubai', points: 15000, cash: 12000, class: 'Business' },
        { route: 'Bangalore → London', destination: 'London', points: 42000, cash: 25000, class: 'Premium Economy' },
        { route: 'Mumbai → Dubai', destination: 'Dubai', points: 14000, cash: 18000, class: 'Economy' },
        { route: 'Hyderabad → Singapore', destination: 'Singapore', points: 22000, cash: 28000, class: 'Business' },
        { route: 'Chennai → Bangkok', destination: 'Bangkok', points: 16000, cash: 22000, class: 'Economy' }
      ];
      
      // Filter out destinations already used and add variety
      const availableRoutes = fallbackRoutes.filter(f => !usedDestinations.has(f.destination));
      
      availableRoutes.slice(0, 3 - diverseRoutes.length).forEach((fallback) => {
        const totalUserPoints = airlinePrograms.reduce((sum, p) => sum + p.points, 0);
        const canAfford = totalUserPoints >= fallback.points;
        const savingsPercent = Math.round(((fallback.cash + fallback.points * 0.5) - fallback.cash) / (fallback.cash + fallback.points * 0.5) * 100);
        
        diverseRoutes.push({
          route: fallback.route,
          class: fallback.class,
          airline: 'American AAdvantage',
          points: fallback.points,
          cash: fallback.cash,
          savings: `${savingsPercent}%`,
          canAfford,
          recommendationReason: canAfford 
            ? `Great value route! Save ${savingsPercent}% vs cash booking` 
            : `Need ${(fallback.points - totalUserPoints).toLocaleString()} more points`
        });
      });
    }

    return diverseRoutes;
  }

  /**
   * Calculate monthly point earning potential
   */
  static calculateEarningPotential(creditCards: LoyaltyProgram[]): {
    monthlyPotential: number;
    yearlyPotential: number;
    topEarningCard: string;
  } {
    if (creditCards.length === 0) {
      return {
        monthlyPotential: 0,
        yearlyPotential: 0,
        topEarningCard: 'No cards added'
      };
    }

    // Estimated monthly spending categories and earning rates
    const spendingProfile = {
      dining: 15000, // INR per month
      travel: 8000,
      groceries: 12000,
      fuel: 5000,
      other: 20000
    };

    const cardEarningRates: Record<string, any> = {
      'HDFC Infinia': { dining: 3.3, travel: 3.3, groceries: 1.65, fuel: 1.65, other: 1.65 },
      'Axis Magnus': { dining: 2.5, travel: 2.5, groceries: 1.25, fuel: 1.25, other: 1.25 },
      'Chase Sapphire': { dining: 2.0, travel: 3.0, groceries: 1.0, fuel: 1.0, other: 1.0 },
      'American Express': { dining: 2.0, travel: 2.0, groceries: 1.0, fuel: 1.0, other: 1.0 }
    };

    let bestCard = creditCards[0].name;
    let maxEarning = 0;

    // Calculate earning for each card
    creditCards.forEach(card => {
      const rates = cardEarningRates[card.name] || { dining: 1, travel: 1, groceries: 1, fuel: 1, other: 1 };
      
      const monthlyEarning = 
        (spendingProfile.dining * rates.dining / 100) +
        (spendingProfile.travel * rates.travel / 100) +
        (spendingProfile.groceries * rates.groceries / 100) +
        (spendingProfile.fuel * rates.fuel / 100) +
        (spendingProfile.other * rates.other / 100);

      if (monthlyEarning > maxEarning) {
        maxEarning = monthlyEarning;
        bestCard = card.name;
      }
    });

    return {
      monthlyPotential: Math.round(maxEarning),
      yearlyPotential: Math.round(maxEarning * 12),
      topEarningCard: bestCard
    };
  }
}

export default CalculationEngine;